import { History } from "history";
import React from "react";
import { FormattedMessage } from "react-intl";

import { asyncTimeout } from "@use/async-timeout";
import { HistoryContext } from "@use/react-router";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { colors, easings } from "@apps/viewer/src/ts/config";

type ProgressionPromptProps = {
    /**
     * Specifies the last location the user was at before revisiting the page
     */
    lastProgressionSrc: string;

    /**
     * When called, then the prompt will be closed.
     */
    closePrompt(): void;
};

type ProgressionPromptState = {
    /**
     * Specifies if we should show a throbber within the continue button (which
     * is enabled in case the user clicks on the continue button).
     */
    showContinueThrobber?: boolean;
};

/**
 * Component responsible for rendering the prompt that should be displayed
 * once the user loads a Course wherein the user already has progressed, in order
 * to give the user the possibility to return the last location he/she was at.
 */
export class ProgressionPromptUnwrapped extends React.PureComponent<
    ProgressionPromptProps & { history?: History },
    ProgressionPromptState
> {
    /**
     * Set up initial state.
     */
    public readonly state: ProgressionPromptState = {};

    /**
     * Main render method
     */
    public render(): React.JSX.Element {
        return (
            <div>
                { this.renderDialogue() }
                <style jsx>{`
                    div {
                        /** Position prompt on top of other content */
                        position: fixed;
                        left: 0;
                        right: 0;
                        top: 0;
                        bottom: 0;
                        z-index: 99;

                        /** Render the backdrop displayed behind the prompt, separating it from other content */
                        &::before {
                            content: "";

                            /** Fill wrapper */
                            position: absolute;
                            left: 0;
                            right: 0;
                            top: 0;
                            bottom: 0;

                            /** Render the actual backdrop */
                            background-color: rgba(255, 255, 255, 0.8);
                        }
                    }

                    @media (max-width: 768px) {
                        div {
                            /**
                             * Position above both progressbar on mobile
                             * in order to allow more space for content
                             */
                            z-index: 1000;
                        }
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Renderer that'll render the dialog itself
     */
    protected renderDialogue(): React.JSX.Element {
        return (
            <dialog open>
                <i>
                    {getIcon("flag", { strokeColor: colors.orange })}
                </i>

                <h2>
                    <FormattedMessage
                        id="ProgressionPrompt__welcome"
                        defaultMessage="Velkommen tilbage!"
                    />
                </h2>
                <span className="introduction">
                    <FormattedMessage
                        id="ProgressionPrompt__introduction"
                        defaultMessage="Du har tidligere arbejdet med dette forløb"
                    />
                </span>

                { this.renderButtons() }

                <style jsx>{`
                    dialog {
                        /** Override defaults */
                        display: block;
                        border: none;
                        margin: 0;
                        background-color: ${ colors.white };

                        /** Setup internal padding */
                        padding: 45px;

                        /** Position in center of screen */
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);

                        /** Setup dimensions of dialog */
                        max-width: 680px;
                        width: calc(100% - 60px);
                        box-sizing: border-box;

                        /** Render box-shadow to separate prompt from content */
                        box-shadow: 0 0 40px rgba(0, 0, 0, 0.25);

                        @media (max-width: 768px) {
                            /**
                             * push down to center around area below the
                             * header on mobile
                             */
                            margin-top: 30px;
                        }
                    }

                    i {
                        /** Center icon in dialog */
                        display: block;
                        margin: 0 auto 35px;

                        /** Setup desired dimensions */
                        width: 54px;
                        height: 65px;
                    }

                    h2 {
                        /** Setup font for title */
                        font-size: 2.25em;
                        font-weight: 700;
                        line-height: 1;

                        /** Setup proper spacing */
                        margin: -5px 0 5px;
                        text-align: center;
                    }

                    .introduction {
                        /** Setup font for introduction */
                        font-size: 1.4em;
                        font-weight: 400;
                        line-height: 1.5;

                        /** Center align */
                        display: block;
                        text-align: center;
                    }

                    /** Setup styles related to mobile */
                    @media (max-width: 768px) {
                        dialog {
                            /** Reduce internal padding to allow more space for content */
                            padding: 25px;
                        }

                        i {
                            /** Reduce icon to allow more room */
                            width: 40px;
                            height: 48px;
                        }

                        h2 {
                            font-size: 1.25em;
                        }

                        .introduction {
                            font-size: 1em;
                        }

                        .action-introduction {
                            font-size: 0.875em;
                        }
                    }
                `}</style>
            </dialog>
        );
    }

    /**
     * Rendered that'll render the buttons associated with the prompt
     */
    // tslint:disable-next-line max-func-body-length
    protected renderButtons(): React.JSX.Element {
        const continueClassName = this.state.showContinueThrobber ? "showThrobber" : "";

        return (
            <div>
                <button id="reset" onClick={ this.props.closePrompt }>
                    <FormattedMessage
                        id="ProgressionPrompt__startOver"
                        defaultMessage="Start forfra"
                    />
                </button>
                <button
                    id="continue"
                    className={ continueClassName }
                    onClick={ this.onContinueClick }
                >
                    <FormattedMessage
                        id="ProgressionPrompt__continue"
                        defaultMessage="Fortsæt"
                    />

                    <i className="throbber" />
                </button>

                <style jsx>{`
                    div {
                        /** Setup space between text and buttons */
                        margin-top: 35px;

                        /** Center align buttons */
                        display: flex;
                        justify-content: center;
                    }

                    button {
                        /** Allow position: absolute to render throbbers */
                        position: relative;

                        /** Override defaults */
                        -webkit-appearance: none;
                        margin: 0;
                        padding: 0;

                        /** Setup desired dimensions */
                        width: 200px;
                        height: 45px;

                        /** Render button properly */
                        border: 2px solid ${ colors.black };

                        /** Indicate that button is clickable */
                        cursor: pointer;

                        /** Setup font */
                        font-size: 1.125em;
                        font-weight: 500;

                        /** Prepare hover transitions */
                        transition:
                            color 0.1s linear,
                            background-color 0.1s linear,
                            border-color 0.1s linear;

                        /** Setup styles specific for the reset button */
                        &#reset {
                            background-color: transparent;
                            color: ${ colors.black };

                            /** Apply hover transition */
                            &:hover {
                                border-color: ${ colors.orange };
                                color: ${ colors.orange };
                            }
                        }

                        & .throbber {
                            /** Center the throbber within the button */
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            margin: -12px -12px;

                            /** Hide the throbber by default */
                            opacity: 0;
                            transform: scale(0.2);

                            /** Prepare appear transition */
                            transition:
                                opacity 0.2s linear,
                                transform 0.15s ${ easings.inOutQuad };

                            /** Render circle using a pseudo element */
                            &::before {
                                /** Set up sizing */
                                box-sizing: border-box;
                                display: block;
                                width: 24px;
                                height: 24px;

                                /** Apply circle styling */
                                border: 2px solid rgba(255, 255, 255, 0.25);
                                border-top-color: ${ colors.white };
                                border-radius: 50%;

                                /** Force rendering of pseudo element */
                                content: "";
                            }
                        }

                        /** Setup styles specific for continue button */
                        &#continue {
                            background-color: ${ colors.orange };
                            border-color: ${ colors.orange };
                            color: ${ colors.white };

                            /** Move away from reset button */
                            margin-left: 45px;

                            /** Apply hover transition */
                            &:hover {
                                background-color: #bb381b !important;
                                border-color: #bb381b !important;
                            }

                            /** Prepare fade-out of text transition */
                            & > :global(span) {
                                transition: opacity 0.15s linear;
                            }

                            /**
                             * Apply transition when throbber should be
                             * displayed.
                             */
                            &.showThrobber {
                                &:hover {
                                    /** Disable hover transition */
                                    background-color: ${ colors.orange } !important;
                                    border-color: ${ colors.orange } !important;
                                    color: ${ colors.white } !important;

                                    /**
                                     * Indicate that element is no longer
                                     * clickable
                                     */
                                    cursor: default;
                                }

                                /** Show the throbber and hide the text */
                                & > .throbber {
                                    opacity: 1;
                                    transform: scale(1);

                                    /** Spin around infinitely */
                                    animation: spin 0.9s linear infinite;
                                }

                                & > :global(span) {
                                    opacity: 0;
                                }
                            }
                        }
                    }

                    /** Setup styles related to rendering on small viewports */
                    @media (max-width: 768px) {
                        div {
                            /** Stack buttons on top of each other if necessary */
                            flex-wrap: wrap;

                            /** Reduce margin between text and first button */
                            margin-top: 25px;
                        }

                        button {
                            font-size: 1em;

                            &#reset {
                                /** Horizontally align self and setup space between self and continue button */
                                margin: 0 auto 15px;
                            }

                            &#continue {
                                /** Horizontally align self. */
                                margin: 0 auto;
                            }
                        }
                    }

                    /** Keyframes to spin the throbber around */
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Listener that's triggered when the user clicks the button to continue
     * from where i left off.
     */
    protected onContinueClick = async() => {
        // If either the history isn't available, which means we simply cannot
        // navigate the user, or if we're already in the process of redirecting
        // the user, then simply bail out!
        if (!this.props.history || this.state.showContinueThrobber) {
            return;
        }

        // Show a throbber within the continue button while we're waiting for
        // the page to load
        this.setState({ showContinueThrobber: true });

        // Ensure the throbber has faded in before replacing history in order to
        // force rendering in Safari
        await asyncTimeout(200);

        // Bring the user to the page where he left off...
        this.props.history.replace(this.props.lastProgressionSrc);
        this.props.closePrompt();
    }
}

/**
 * Helper that'll inject a reference to the history manager used within the
 * application lifecycle.
 */
// tslint:disable-next-line variable-name
export const ProgressionPrompt: React.FC<ProgressionPromptProps> = (props) => (
    <HistoryContext.Consumer>
        { (history) => <ProgressionPromptUnwrapped history={ history } { ...props } /> }
    </HistoryContext.Consumer>
);
